// AI F §1.1.5 (list, filters, sorting), §1.1.6 (storage usage), §1.1.3 (live status)
import Vue from 'vue';
import KbSourcesAPI from '../../api/kbSources';

export const state = {
  records: [],
  meta: {},
  selectedSource: null,
  // AI F §1.1.5 — filters by Type, Status, Last Updated and Size
  filters: {
    status: [],
    sourceType: [],
    updatedFrom: '',
    updatedTo: '',
    sizeFrom: '',
    sizeTo: '',
  },
  sortConfig: { key: 'content_updated_at', order: 'desc' },
  uiFlags: { isFetching: false, isFetchingItem: false, isSearching: false },
};

export const getters = {
  getRecords: $state => $state.records,
  getMeta: $state => $state.meta,
  getSelectedSource: $state => $state.selectedSource,
  getFilters: $state => $state.filters,
  getSortConfig: $state => $state.sortConfig,
  getUIFlags: $state => $state.uiFlags,
  // AI F §1.1.6 — the banner shows from 80%, everything is blocked at 100%
  getStorageUsage: $state => {
    const { storage_used_bytes: used = 0, storage_quota_bytes: quota = 0 } =
      $state.meta;
    return { used, quota, percentage: quota ? (used * 100) / quota : 0 };
  },
};

// AI F §1.1.5
let listRequest = 0;

export const actions = {
  async fetch({ commit, state: $state }, { page = 1 } = {}) {
    listRequest += 1;
    const request = listRequest;
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { key, order } = $state.sortConfig;
      const { data } = await KbSourcesAPI.list({
        page,
        sort: `${order === 'desc' ? '-' : ''}${key}`,
        status: $state.filters.status,
        sourceType: $state.filters.sourceType,
        updatedFrom: $state.filters.updatedFrom,
        updatedTo: $state.filters.updatedTo,
        sizeFrom: $state.filters.sizeFrom,
        sizeTo: $state.filters.sizeTo,
      });
      // AI F §1.1.5
      if (request === listRequest) commit('SET_RECORDS', data);
    } finally {
      if (request === listRequest) commit('SET_UI_FLAG', { isFetching: false });
    }
  },
  async show({ commit }, id) {
    commit('SET_UI_FLAG', { isFetchingItem: true });
    try {
      const { data } = await KbSourcesAPI.show(id);
      commit('SET_SELECTED_SOURCE', data);
    } finally {
      commit('SET_UI_FLAG', { isFetchingItem: false });
    }
  },
  async search({ commit }, query) {
    commit('SET_UI_FLAG', { isSearching: true });
    try {
      const { data } = await KbSourcesAPI.search(query);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isSearching: false });
    }
  },
  // AI F §1.5.1, §1.3.2, §1.4.2 — custom answers, website URLs and live searches
  async create({ dispatch }, payload) {
    const { data } = await KbSourcesAPI.create({ kb_source: payload });
    await dispatch('fetch');
    return data;
  },
  // AI F §1.2.2 — one request per file; the server checks size, format and quota
  async uploadFile({ dispatch }, file) {
    const form = new FormData();
    form.append('kb_source[source_type]', 'file');
    form.append('kb_source[file]', file);
    const { data } = await KbSourcesAPI.upload(form);
    await dispatch('fetch');
    return data;
  },
  // AI F §1.2.5 — manual re-index of the stored text
  async reindex({ dispatch }, id) {
    const { data } = await KbSourcesAPI.reindex(id);
    await dispatch('fetch');
    return data;
  },
  // AI F §1.1.2, §1.2.3 — extract the stored original again, replacing the text
  async reextract({ dispatch }, id) {
    const { data } = await KbSourcesAPI.reextract(id);
    await dispatch('fetch');
    return data;
  },
  // AI F §1.1.8 — archiving takes the source out of search; it is deleted for good after 30 days
  async archive({ commit, dispatch }, id) {
    const { data } = await KbSourcesAPI.delete(id);
    commit('SET_SELECTED_SOURCE', data);
    await dispatch('fetch');
    return data;
  },
  async restore({ commit, dispatch }, id) {
    const { data } = await KbSourcesAPI.restore(id);
    commit('SET_SELECTED_SOURCE', data);
    await dispatch('fetch');
    return data;
  },
  async update({ commit, dispatch }, { id, ...payload }) {
    const { data } = await KbSourcesAPI.update(id, { kb_source: payload });
    commit('SET_SELECTED_SOURCE', data);
    await dispatch('fetch');
    return data;
  },
  setFilters({ commit, dispatch }, filters) {
    commit('SET_FILTERS', filters);
    return dispatch('fetch');
  },
  setSort({ commit, dispatch }, sortConfig) {
    commit('SET_SORT_CONFIG', sortConfig);
    return dispatch('fetch');
  },
  clearSelected({ commit }) {
    commit('SET_SELECTED_SOURCE', null);
  },
  // AI F §1.1.3 — kb.source.updated from ActionCable
  updateFromPush({ commit, dispatch, state: $state }, source) {
    const openStamp =
      $state.selectedSource?.id === source.id &&
      $state.selectedSource.updated_at;
    commit('UPDATE_RECORD', source);
    if (openStamp && openStamp !== source.updated_at) {
      dispatch('refreshSelected', source.id);
    }
  },
  async refreshSelected({ commit, state: $state }, id) {
    const { data } = await KbSourcesAPI.show(id);
    if ($state.selectedSource?.id === id) commit('SET_SELECTED_SOURCE', data);
  },
};

export const mutations = {
  SET_RECORDS($state, { payload, meta }) {
    $state.records = payload;
    $state.meta = meta;
  },
  UPDATE_RECORD($state, source) {
    const index = $state.records.findIndex(record => record.id === source.id);
    if (index > -1) {
      Vue.set($state.records, index, { ...$state.records[index], ...source });
    }
    if ($state.selectedSource?.id === source.id) {
      $state.selectedSource = { ...$state.selectedSource, ...source };
    }
  },
  SET_SELECTED_SOURCE($state, source) {
    $state.selectedSource = source;
  },
  SET_FILTERS($state, filters) {
    $state.filters = { ...$state.filters, ...filters };
  },
  SET_SORT_CONFIG($state, sortConfig) {
    $state.sortConfig = sortConfig;
  },
  SET_UI_FLAG($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
};

export default { namespaced: true, state, getters, actions, mutations };
